/**
 * ClientDocumentsTab — the client-detail "Dokumente" tab grid (dms/spec.md
 * §4.2, §4.6, §M12). Active/latest-version-only documents for this client
 * (`GET /documents?client_id=&state=active`).
 *
 * Clickability rule (§4.6): rows are NOT clickable as a whole — mirrors the
 * Ablage grid's OWN convention (§4.1: "Dokument … the clickable element"),
 * since this tab is explicitly "Same grid, filtered" (§4.2), not the
 * Policen-Nr.-only convention that applies to the Policen tab/Policies page.
 * Only the Dokument (title) cell navigates, to the M11 inline viewer
 * (`/ablage/$id`) — reused as-is, no second viewer.
 *
 * [Source: apps/web/src/features/documents/DocumentsScreen.tsx] (column/clickability precedent)
 * [Source: projects/broker-ag/dms/spec.md §4.2, §4.6, §M12]
 */
import type { ColumnDef } from '@tanstack/react-table';
import { useNavigate } from '@tanstack/react-router';
import { DataTable } from '../../../components/data/DataTable';
import { EntityLink } from '../../../components/ui/EntityLink';
import { Badge } from '../../../components/ui/Badge';
import { useClientDocumentRows, type ClientDocumentRow } from '../../documents/hooks/useClientDocumentRows';

export function ClientDocumentsTab({ clientId }: { clientId: string }) {
  const navigate = useNavigate();
  const { rows, isLoading, isError } = useClientDocumentRows(clientId);

  const columns: ColumnDef<ClientDocumentRow>[] = [
    {
      accessorKey: 'title',
      header: 'Dokument',
      // Clickability rule (dms/spec.md §4.6): ONLY this column navigates — same
      // convention as the Ablage grid this tab is a filtered slice of.
      cell: ({ row }) => (
        <EntityLink
          onActivate={() => void navigate({ to: '/ablage/$id', params: { id: row.original.id } })}
          className="font-medium"
        >
          {row.original.title}
        </EntityLink>
      ),
    },
    {
      accessorKey: 'category',
      header: 'Kategorie',
      cell: ({ row }) =>
        row.original.category ? (
          <Badge variant="neutral">{row.original.category}</Badge>
        ) : (
          <span className="text-muted-foreground">-</span>
        ),
    },
    {
      accessorKey: 'quelle',
      header: 'Quelle',
      cell: ({ row }) => <span className="text-muted-foreground">{row.original.quelle}</span>,
    },
    {
      accessorKey: 'dateDisplay',
      header: 'Datum',
      cell: ({ row }) => <span className="mono-num text-xs">{row.original.dateDisplay}</span>,
    },
    {
      accessorKey: 'sizeDisplay',
      header: 'Grösse',
      cell: ({ row }) => <div className="text-right mono-num">{row.original.sizeDisplay}</div>,
    },
  ];

  if (isLoading) {
    return (
      <div className="rounded-lg border bg-card p-8 text-center text-sm text-muted-foreground">
        Lade Dokumente …
      </div>
    );
  }
  if (isError) {
    return (
      <div role="alert" className="rounded-lg border bg-card p-8 text-center text-sm text-muted-foreground">
        Dokumente konnten nicht geladen werden.
      </div>
    );
  }
  if (rows.length === 0) {
    return (
      <div className="rounded-lg border bg-card p-8 text-center text-sm text-muted-foreground">
        Keine Dokumente abgelegt.
      </div>
    );
  }

  return <DataTable columns={columns} data={rows} />;
}
